// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CloseRounded from '@mui/icons-material/CloseRounded';

// material ui
import { Chip } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

export default function ChatDrawer({
  open,
  onClose,
  title,
  width = 800,
  anchor = 'right',
  children,
}) {
  const toggle = (next) => () => onClose(next);

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={toggle(false)}
      hideBackdrop={false}
      ModalProps={{ keepMounted: true }}
      sx={{ zIndex: (t) => t.zIndex.modal }}
      slotProps={{
        paper: {
          sx: (theme) => ({
            top: 0,
            m: { xs: 1, sm: 2 },
            maxWidth: width,
            width:
              anchor === 'left' || anchor === 'right'
                ? {
                    xs: `calc(100% - ${theme.spacing(2)})`,
                    sm: `calc(100% - ${theme.spacing(2)})`,
                    xl: `calc(100% - ${theme.spacing(2)})`,
                  }
                : undefined,
            height:
              anchor === 'top' || anchor === 'bottom'
                ? {
                    xs: `calc(100% - ${theme.spacing(2)})`,
                    sm: `calc(100% - ${theme.spacing(4)})`,
                  }
                : `calc(100% - ${theme.spacing(4)})`,
            borderRadius: 3,
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[50]
                : theme.palette.grey[900],
            boxShadow: 24,
            overflow: 'hidden', // Prevent drawer itself from scrolling
          }),
        }
      }}
    >
      <ChatDrawerContent onClose={toggle(false)} title={title}>
        {children}
      </ChatDrawerContent>
    </Drawer>
  );
}

function ChatDrawerContent({ onClose, title = 'Chat', children }) {
  const { t } = useTranslation('ai-assistant');
  return (
    <Box
      role="presentation"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%', // Full height to contain chat layout
        overflow: 'hidden', // Prevent content container from scrolling
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[50]
              : theme.palette.grey[900],
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0, // Prevent header from shrinking
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
          <Chip
            label={t('beta')}
            variant="outlined"
            color="success"
            size="small"
          />
        </Box>
        <IconButton onClick={onClose} aria-label={t('close')}>
          <CloseRounded />
        </IconButton>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // Let child (ChatComponent) manage its own scrolling
          minHeight: 0, // Important for flexbox scrolling to work properly
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
